/**
 * Local Login settings management page component for admins.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useSettings } from '../contexts/SettingsContext';
import { updateSettings, SettingsUpdate } from '../api/settings';
import './LocalLoginPage.css';

export const LocalLoginPage: React.FC = () => {
  const { settings, refreshSettings } = useSettings();
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [signupRequiresApproval, setSignupRequiresApproval] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setSignupEnabled(settings.signup_enabled);
      setSignupRequiresApproval(settings.signup_requires_approval);
    }
  }, [settings]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const updates: SettingsUpdate = {
        signup_enabled: signupEnabled,
        signup_requires_approval: signupRequiresApproval,
      };

      await updateSettings(updates);
      setSuccess('Local login settings updated successfully!');
      await refreshSettings();
    } catch (err: any) {
      let errorMessage = 'Failed to update local login settings. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setSignupEnabled(settings.signup_enabled);
      setSignupRequiresApproval(settings.signup_requires_approval);
      setError(null);
      setSuccess(null);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all local login settings to defaults? This cannot be undone.')) {
      setSignupEnabled(false);
      setSignupRequiresApproval(true);
      setError(null);
      setSuccess(null);
    }
  };

  if (!settings) {
    return (
      <AdminLayout>
        <div className="local-login-page">
          <div className="local-login-container">
            <div className="loading-message">Loading settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="local-login-page">
        <div className="local-login-container">
          <div className="admin-breadcrumb">
            <Link to="/admin">Admin</Link>
            <span className="admin-breadcrumb-separator">/</span>
            <span className="admin-breadcrumb-current">Local Login</span>
          </div>
          <h1>Local Login Settings</h1>

          <form onSubmit={handleSubmit} className="local-login-form" role="form">
            {error && (
              <div className="error-message" role="alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            {success && (
              <div className="success-message" role="alert">
                {success}
              </div>
            )}

            <section className="settings-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={signupEnabled}
                    onChange={(e) => {
                      setSignupEnabled(e.target.checked);
                      setError(null);
                    }}
                    disabled={isLoading}
                  />
                  <span>Enable User Sign-Up</span>
                </label>
                <small className="form-hint">
                  Allow new users to create accounts through the sign-up page
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={signupRequiresApproval}
                    onChange={(e) => {
                      setSignupRequiresApproval(e.target.checked);
                      setError(null);
                    }}
                    disabled={isLoading || !signupEnabled}
                  />
                  <span>Require Admin Approval for Sign-Ups</span>
                </label>
                <small className="form-hint">
                  New users will be inactive until approved by an administrator
                </small>
              </div>
            </section>

            <div className="form-actions">
              <button
                type="button"
                className="btn-reset"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Changes
              </button>
              <button
                type="button"
                className="btn-reset-defaults"
                onClick={handleResetToDefaults}
                disabled={isLoading}
              >
                Reset to Defaults
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

